import {
  requestLocalStorageGet,
  requestLocalStorageSet,
  requestLocalStorageRemove,
  requestLocalStorageClear,
  requestLocalStorageKeys,
  requestLocalStorageHas,
  sendLocalStorageResponse,
  sendLocalStorageChanged,
} from '../actions'
import { sendMessage } from '../../../../common/message/sendMessage'
import { listenMessage } from '../../../../common/message/listenMessage'
import { localStorage as createLocalStorage } from 'helpers/localStorage'
import { PayloadAction } from '../../helpers/createAction'
import { isInIframe } from 'helpers/isInIframe'

const createId = () => {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random()}`
  }
}

const eventName = 'wlsy:remoteLocalStorageChange'

const emitLocalStorageChanged = (detail: {
  key?: string
  value?: string | null
  operation: 'set' | 'remove' | 'clear'
  source?: 'host' | 'remote'
}) => {
  window.dispatchEvent(
    new CustomEvent(eventName, {
      detail,
    }),
  )
}

const remoteKey = (scopePrefix: string | undefined, key: string) => {
  const scopePart = scopePrefix ? `:${scopePrefix}` : ''
  return `remote${scopePart}:${key}`
}

export const remoteLocalStorage = (scopePrefix?: string) => {
  const storage = createLocalStorage()
  const isRemote = isInIframe()

  const pending = new Map<
    string,
    {
      resolve: (value: unknown) => void
      reject: (error: Error) => void
      timeoutId: number
    }
  >()

  const ensureRemoteListener = (() => {
    let unsubscribe: null | (() => void) = null

    return () => {
      if (!isRemote || unsubscribe) return

      unsubscribe = listenMessage({
        sourceWindow: window.parent,
        onMessage: (action) => {
          if (sendLocalStorageResponse.match(action)) {
            const { id, value, success, error } = (
              action as unknown as ReturnType<typeof sendLocalStorageResponse>
            ).payload
            const entry = pending.get(id)
            if (!entry) return

            pending.delete(id)
            window.clearTimeout(entry.timeoutId)

            if (success) entry.resolve(value)
            else entry.reject(new Error(error || 'Unknown error'))
          }

          if (sendLocalStorageChanged.match(action)) {
            emitLocalStorageChanged(
              (action as unknown as ReturnType<typeof sendLocalStorageChanged>)
                .payload,
            )
          }
        },
        whitelist: [sendLocalStorageResponse, sendLocalStorageChanged],
      })
    }
  })()

  const sendRequest = (action: PayloadAction<Record<string, unknown>>) => {
    ensureRemoteListener()

    sendMessage({
      action,
      windowElement: window.parent,
      whitelist: [
        requestLocalStorageGet,
        requestLocalStorageSet,
        requestLocalStorageRemove,
        requestLocalStorageClear,
        requestLocalStorageKeys,
        requestLocalStorageHas,
      ],
    })
  }

  const createRequest = <T, P extends { id: string }>(
    actionCreator: (payload: P) => PayloadAction<P>,
    payload: Omit<P, 'id'>,
  ): Promise<T> => {
    const id = createId()

    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        pending.delete(id)
        reject(new Error('Remote localStorage request timed out'))
      }, 8000)

      pending.set(id, {
        resolve,
        reject,
        timeoutId,
      })

      sendRequest(actionCreator({ id, ...payload } as P))
    })
  }

  const getItem = async (key: string) => {
    const fullKey = remoteKey(scopePrefix, key)

    if (!isRemote) {
      return storage.getItem(fullKey) ?? null
    }

    return createRequest<string | null, { id: string; key: string }>(
      requestLocalStorageGet,
      { key: fullKey },
    )
  }

  const setItem = async (key: string, value: string) => {
    const fullKey = remoteKey(scopePrefix, key)

    if (!isRemote) {
      storage.setItem(fullKey, value)
      emitLocalStorageChanged({
        key: fullKey,
        value,
        operation: 'set',
        source: 'host',
      })
      return
    }

    await createRequest<string, { id: string; key: string; value: string }>(
      requestLocalStorageSet,
      { key: fullKey, value },
    )
    emitLocalStorageChanged({ key: fullKey, value, operation: 'set' })
  }

  const removeItem = async (key: string) => {
    const fullKey = remoteKey(scopePrefix, key)

    if (!isRemote) {
      storage.removeItem(fullKey)
      emitLocalStorageChanged({
        key: fullKey,
        value: null,
        operation: 'remove',
        source: 'host',
      })
      return
    }

    await createRequest<null, { id: string; key: string }>(
      requestLocalStorageRemove,
      { key: fullKey },
    )
    emitLocalStorageChanged({ key: fullKey, value: null, operation: 'remove' })
  }

  const clear = async () => {
    const scope = `remote${scopePrefix ? `:${scopePrefix}` : ''}`
    const prefix = `${scope}:`

    if (!isRemote) {
      storage
        .keys()
        .filter((k) => k.startsWith(prefix))
        .forEach((k) => storage.removeItem(k))
      emitLocalStorageChanged({ operation: 'clear', source: 'host' })
      return
    }

    await createRequest<null, { id: string; prefix?: string }>(
      requestLocalStorageClear,
      { prefix },
    )
    emitLocalStorageChanged({ operation: 'clear' })
  }

  const keys = async () => {
    const scope = `remote${scopePrefix ? `:${scopePrefix}` : ''}`
    const prefix = `${scope}:`

    if (!isRemote) {
      return storage
        .keys()
        .filter((k) => k.startsWith(prefix))
        .map((k) => k.slice(prefix.length))
    }

    const allKeys = await createRequest<string[], { id: string }>(
      requestLocalStorageKeys,
      {},
    )
    return allKeys
      .filter((k) => k.startsWith(prefix))
      .map((k) => k.slice(prefix.length))
  }

  const has = async (key: string) => {
    const fullKey = remoteKey(scopePrefix, key)

    if (!isRemote) return storage.has(fullKey)

    return createRequest<boolean, { id: string; key: string }>(
      requestLocalStorageHas,
      { key: fullKey },
    )
  }

  const getJSON = async <T = unknown>(key: string): Promise<T | null> => {
    const raw = await getItem(key)
    if (raw === null || raw === undefined) return null
    try {
      return JSON.parse(raw) as T
    } catch {
      return null
    }
  }

  const setJSON = async (key: string, value: unknown): Promise<void> => {
    try {
      await setItem(key, JSON.stringify(value))
    } catch {
      return
    }
  }

  const subscribe = (
    cb: (detail: {
      key?: string
      value?: string | null
      operation: string
    }) => void,
  ) => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        key?: string
        value?: string | null
        operation: string
      }

      cb(detail)
    }

    window.addEventListener(eventName, handler as EventListener)

    return () => {
      window.removeEventListener(eventName, handler as EventListener)
    }
  }

  return {
    getItem,
    setItem,
    removeItem,
    clear,
    keys,
    has,
    getJSON,
    setJSON,
    subscribe,
  }
}
